// Libraries
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

// Layout
import ProductionLayout from 'layouts/production/Pages/SingleProduction/ProductionLayout';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';

// @mui material components
import Grid from '@mui/material/Grid';

// Production Pages And Components
import MainPage from 'layouts/production/Pages/SingleProduction/MainPage';
import EmailPage from 'layouts/production/Pages/SingleProduction/EmailPage';
import AaroOrderPage from 'layouts/production/Pages/SingleProduction/Aaro';
import AaroContractPage from 'layouts/production/Pages/SingleProduction/AaroConract';

import AaroLoadingScreen from 'components/Loading';

// API
import { getProduction } from 'services/api/production';

// Hooks
import { useOrderQueries } from 'hooks/aaro/useOrderQueries';
import { useContractQueries } from 'hooks/aaro/useContractQueries';

// Components
import ProjectPage from 'layouts/projects/Pages/SingleProject/index.js';

const fetchProduction = async (id) => {
    try {
        const response = await getProduction(id)
            .then((rp) => rp.data.result)
            .catch((err) => {
                console.error('Error fetching production:', err);
                throw err;
            });
        return response;
    } catch (error) {
        console.error('Error in fetchProduction:', error);
        throw error;
    }
};

function GeneralPage({ productionId, isDialog = false }) {
    // Use productionId prop if provided (dialog mode), otherwise use URL params
    const { id: urlId } = useParams();
    const id = productionId || urlId;
    const [value, setValue] = useState(0);

    const orderQueries = useOrderQueries({ id: id, type: 'DekontID', TabloID: 'DekontFatura', TabloSatirID: id });

    // Get contract ID from orderDetails if available - using useMemo to avoid recalculations
    const contractID = useMemo(() => {
        return orderQueries?.orderDetailsQuery?.data?.[0]?.SozlesmeID || null;
    }, [orderQueries?.orderDetailsQuery?.data]);

    // Always call the hook, but pass contractID or null
    const contractQueries = useContractQueries(contractID || null);

    // Combine all queries for error and loading states
    const aaroQueries = { ...orderQueries, ...contractQueries };

    const isLoadingAaro = Object.values(aaroQueries).some((query) => query?.isLoading);
    const errorAaro = Object.values(aaroQueries).find((query) => query?.error)?.error;

    // Production Query
    const { data, isLoading, error } = useQuery({
        queryKey: ['fetchProduction', id],
        queryFn: () => fetchProduction(id),
        retry: 2,
        onError: (err) => {
            console.error('Query error:', err);
        },
    });

    if (isLoading) return <AaroLoadingScreen />;
    if (error) return <div>Bir Hata Oluştu: {error.message || 'Üretim verileri yüklenemedi'}</div>;
    if (isLoadingAaro) return <AaroLoadingScreen />;
    if (!data) return <div>Üretim bulunamadı</div>;

    const handlePageChanges = (pageNumber) => {
        switch (pageNumber) {
            case 0:
                return <MainPage production={data} relatedID={id} aaroQueries={aaroQueries} setValue={setValue} />;
            case 1:
                return <AaroOrderPage production={data} queries={aaroQueries} />;
            case 2:
                return <AaroContractPage production={data} aaroQueries={aaroQueries} />;
            case 3:
                return <ProjectPage projectId={data?.project?._id} isDialog={true} />;
            case 4:
                return <EmailPage production={data} />;

            default:
                return <div>Sayfa bulunamadı</div>;
        }
    };

    return (
        <ProductionLayout setValue={setValue} value={value} isDialog={isDialog} production={data}>
            <MDBox mt={isDialog ? 0 : 0}>
                <Grid container spacing={3}>
                    <Grid size={12}>
                        <MDBox mb={3}>{handlePageChanges(value)}</MDBox>
                    </Grid>
                </Grid>
            </MDBox>
        </ProductionLayout>
    );
}

export default GeneralPage;
